import { TextUniDriver, textUniDriverFactory } from '../Text/Text.uni.driver';
import { checkboxUniDriverFactory } from '../Checkbox/Checkbox.uni.driver';
import {
  baseUniDriverFactory,
  getDataAttributeValue,
} from '../utils/test-utils/unidriver';
import { dataAttr, sizes, shapes } from './constants';
import { UniDriver } from '@wix/unidriver-core';
import { BaseUniDriver } from '@wix/wix-ui-test-utils/unidriver';
import { tooltipDriverFactory } from '../Tooltip/Tooltip.uni.driver';

export interface SelectorUniDriver extends BaseUniDriver {
  isImageTiny: () => Promise<boolean>;
  isImageSmall: () => Promise<boolean>;
  isImagePortrait: () => Promise<boolean>;
  isImageLarge: () => Promise<boolean>;
  isImageCinema: () => Promise<boolean>;
  isImageCircle: () => Promise<boolean>;
  isImageRectangular: () => Promise<boolean>;
  isDisabled: () => Promise<boolean>;
  toggleType: () => Promise<string>;
  isChecked: () => Promise<boolean>;
  isIndeterminate: () => Promise<boolean>;
  hasImage: () => Promise<boolean>;
  getImage: () => Promise<HTMLImageElement>;
  titleTextDriver: () => TextUniDriver;
  subtitleTextDriver: () => TextUniDriver;
  hasExtraNode: () => Promise<boolean>;
  getExtraNode: () => Promise<HTMLElement>;
  toggle: () => Promise<void>;
  getTooltipText: () => Promise<string | null>;
}

export const selectorUniDriverFactory = (
  base: UniDriver,
  body: UniDriver,
): SelectorUniDriver => {
  const toggleInput = () => base.$('[data-hook="toggle"] input');
  const image = () => base.$('[data-hook="selector-image"]');
  const titleTextDriver = () =>
    textUniDriverFactory(base.$('[data-hook="selector-title"]'));
  const subtitleTextDriver = () =>
    textUniDriverFactory(base.$('[data-hook="selector-subtitle"]'));
  const extraNode = () => base.$('[data-hook="selector-extra-node"]');

  const checkboxDriver = () =>
    checkboxUniDriverFactory(base.$('[data-hook="toggle"]'), base);

  const getTooltipDriver = () => {
    return tooltipDriverFactory(base.$('[data-hook="toggle-tooltip"]'), body);
  };

  return {
    ...baseUniDriverFactory(base),

    isImageTiny: async () =>
      (await getDataAttributeValue(base, dataAttr.SIZE)) === sizes.TINY,
    isImageSmall: async () =>
      (await getDataAttributeValue(base, dataAttr.SIZE)) === sizes.SMALL,
    isImagePortrait: async () =>
      (await getDataAttributeValue(base, dataAttr.SIZE)) === sizes.PORTRAIT,
    isImageLarge: async () =>
      (await getDataAttributeValue(base, dataAttr.SIZE)) === sizes.LARGE,
    isImageCinema: async () =>
      (await getDataAttributeValue(base, dataAttr.SIZE)) === sizes.CINEMA,
    isImageCircle: async () =>
      (await getDataAttributeValue(base, dataAttr.SHAPE)) === shapes.CIRCLE,
    isImageRectangular: async () =>
      (await getDataAttributeValue(base, dataAttr.SHAPE)) ===
      shapes.RECTANGULAR,
    isDisabled: () => toggleInput().prop('disabled'),
    toggleType: () => toggleInput().prop('type'),
    isChecked: async () =>
      (await toggleInput().exists()) && toggleInput().prop('checked'),
    isIndeterminate: () => checkboxDriver().isIndeterminate(),
    hasImage: () => image().exists(),
    getImage: async () => image().prop('firstChild'),
    titleTextDriver,
    subtitleTextDriver,
    hasExtraNode: () => extraNode().exists(),
    getExtraNode: () => extraNode().prop('firstChild'),
    toggle: () => base.click(),
    getTooltipText: async () => {
      const tooltip = getTooltipDriver();
      if (!(await tooltip.exists())) {
        return null;
      }
      return tooltip.getTooltipText();
    },
  };
};
